<template>
  <div class="details-page-wrapper">
    <!-- COMPACT TOP NAVIGATION BAR -->
    <header class="details-topbar">
      <div class="topbar-inner">
        <button
          class="topbar-back-btn"
          @click="goHome"
          title="Back to QR Scanner"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>

        <div class="topbar-title-wrap">
          <h1 class="topbar-title">Ticket Verification</h1>
          <div class="topbar-code-chip" v-if="qrcode" :title="qrcode">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span class="topbar-code-text">{{ qrcode }}</span>
          </div>
        </div>

        <button
          class="topbar-action-btn"
          @click="goToShow"
          title="Show Schedules"
        >
          <img src="@/assets/img/reel.svg" alt="Shows" width="20" height="20" />
        </button>
      </div>
    </header>

    <div class="details-container" v-if="!isLoading">
      <!-- COMPACT STATUS HERO BANNER -->
      <section
        class="status-hero-card"
        :class="{
          'status-valid': showStatusInfo.isValid,
          'status-warning':
            !showStatusInfo.isValid &&
            (showStatusInfo.code === 'ENTRY_NOT_STARTED' ||
              showStatusInfo.code === 'LATE_ENTRY'),
          'status-error':
            !showStatusInfo.isValid &&
            (showStatusInfo.code === 'SHOW_PASSED' ||
              showStatusInfo.code === 'CINEMA_MISMATCH' ||
              showStatusInfo.code === 'API_ERROR')
        }"
      >
        <div class="status-hero-icon-box">
          <!-- VALID ENTRY -->
          <svg
            v-if="showStatusInfo.isValid"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>

          <!-- ENTRY NOT STARTED -->
          <svg
            v-else-if="showStatusInfo.code === 'ENTRY_NOT_STARTED'"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>

          <!-- LATE ENTRY / SHOW IN PROGRESS -->
          <svg
            v-else-if="showStatusInfo.code === 'LATE_ENTRY'"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
            <path d="M19 12h-3"></path>
          </svg>

          <!-- CINEMA MISMATCH -->
          <svg
            v-else-if="showStatusInfo.code === 'CINEMA_MISMATCH'"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <line x1="15" y1="8" x2="9" y2="14"></line>
            <line x1="9" y1="8" x2="15" y2="14"></line>
          </svg>

          <!-- SHOW PASSED / DEFAULT -->
          <svg
            v-else
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>

        <div class="status-hero-text">
          <div class="status-hero-headline">
            {{ showStatusInfo.message }}
          </div>
          <div class="status-hero-sub" v-if="showStatusInfo.subMessage">
            {{ showStatusInfo.subMessage }}
          </div>
        </div>

        <!-- OPERATOR BUTTON: ALLOW ENTRY ON LATE ARRIVAL OR ENTRY NOT STARTED -->
        <button
          v-if="
            (showStatusInfo.code === 'LATE_ENTRY' ||
              showStatusInfo.code === 'ENTRY_NOT_STARTED') &&
              !isLateEntryAllowedByOperator &&
              !isEarlyEntryAllowedByOperator
          "
          class="status-hero-allow-btn"
          @click="allowEntryOverride"
          type="button"
          title="Allow Entry for this show"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>Allow Entry</span>
        </button>

        <!-- RE-OPEN POPUP BUTTON IF WARNING/ERROR -->
        <button
          v-if="!showStatusInfo.isValid"
          class="status-hero-review-btn"
          @click="openValidationPopup"
          title="View detailed warning"
        >
          Details
        </button>
      </section>

      <!-- RESCHEDULED SHOW ALERT (IF ANY) -->
      <section
        class="swap-alert-card"
        v-if="
          bookingDetails.swapAlert &&
            bookingDetails.swapDetails &&
            bookingDetails.swapDetails.length > 0
        "
      >
        <div class="swap-alert-header">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#b45309"
            stroke-width="2.5"
          >
            <path
              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            ></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>Rescheduled Show</span>
        </div>

        <div class="swap-alert-body">
          <p v-if="bookingDetails.swap_type == 1" class="swap-text">
            Shifted from
            <strong>{{ bookingDetails.swapDetails[0].screen_name }}</strong>
            to
            <strong>{{ bookingDetails.screen_name }}</strong>
          </p>

          <p v-if="bookingDetails.swap_type == 2" class="swap-text">
            Originally
            <strong>
              {{ bookingDetails.swapDetails[0].ss_start_show_time }} -
              {{ bookingDetails.swapDetails[0].ss_end_show_time }} |
              {{ bookingDetails.swapDetails[0].screen_name }}
            </strong>
            rescheduled to
            <strong>
              {{ bookingDetails.ss_start_show_time }} -
              {{ bookingDetails.ss_end_show_time }} |
              {{ bookingDetails.screen_name }}
            </strong>
          </p>
        </div>
      </section>

      <!-- GLANCEABLE MOVIE & SCREEN SUMMARY CARD -->
      <section class="card movie-hero-card">
        <div class="movie-hero-top">
          <div class="movie-title-group">
            <h2 class="movie-title">
              {{ bookingDetails.movie_title || bookingDetails.movie_name }}
            </h2>

            <div class="movie-tags-row">
              <span
                class="badge-tag tag-lang"
                v-if="
                  bookingDetails.lang_version ||
                    bookingDetails.lang_name ||
                    bookingDetails.version_name
                "
              >
                {{
                  bookingDetails.lang_version ||
                    bookingDetails.lang_name ||
                    bookingDetails.version_name
                }}
              </span>

              <span class="badge-tag tag-format" v-if="bookingDetails.mf_name">
                {{ bookingDetails.mf_name }}
              </span>

              <span class="badge-tag tag-cinema-sub">
                {{
                  bookingDetails.cine_name ||
                    bookingDetails.cinema_name ||
                    "Cinema Hall"
                }}
              </span>
            </div>
          </div>

          <!-- CENSOR / AGE RATING BADGE -->
          <div class="age-badge-wrapper" v-if="movieRating !== ''">
            <div
              class="rating-circle"
              :style="{
                backgroundColor: getAgeColor(
                  bookingDetails.age_limit || movieRating
                )
              }"
            >
              <span class="rating-val">{{ movieRating }}</span>
              <span class="rating-sub" v-if="showAgeLabel">Age</span>
            </div>
            <span class="dob-note" v-if="bookingDetails.earliest_bday">
              DOB &lt; {{ bookingDetails.earliest_bday }}
            </span>
          </div>
        </div>

        <!-- QUICK GLANCE PILLS (TIME, SCREEN, SEATS) -->
        <div class="quick-glance-pills">
          <!-- TIME & DATE -->
          <div class="glance-pill pill-time">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#032d6e"
              stroke-width="2.5"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <div class="glance-pill-text">
              <span class="glance-main-val">{{
                bookingDetails.ss_start_show_time || "-"
              }}</span>
              <span class="glance-sub-val" v-if="bookingDetails.showdate">{{
                bookingDetails.showdate
              }}</span>
            </div>
          </div>

          <!-- SCREEN / AUDI -->
          <div class="glance-pill pill-screen">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0f172a"
              stroke-width="2.5"
            >
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
            <div class="glance-pill-text">
              <span class="glance-main-val">{{
                bookingDetails.screen_name || "Screen"
              }}</span>
              <span class="glance-sub-val">{{
                bookingDetails.mf_name || "Hall"
              }}</span>
            </div>
          </div>

          <!-- SEATS SUMMARY (FULL WIDTH ON MOBILE, NEVER OVERFLOWS!) -->
          <div class="glance-pill pill-seats">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1d4ed8"
              stroke-width="2.5"
            >
              <path
                d="M4 18v3M20 18v3M4 10a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2M6 14h12v4H6z"
              ></path>
            </svg>
            <div class="glance-pill-text">
              <div class="pill-seats-headline">
                <span class="glance-main-val">
                  {{
                    bookingDetails.isPrivateShow
                      ? "Private Hall"
                      : seats.length + (seats.length === 1 ? " Seat" : " Seats")
                  }}
                </span>
                <span class="pill-seats-dot" v-if="computedSeatNames">·</span>
                <span
                  class="glance-sub-val pill-seats-highlight"
                  v-if="computedSeatNames"
                >
                  {{ computedSeatNames }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- TICKETS & SEATS COMPACT CARD -->
      <section class="card tickets-selection-card">
        <div class="section-card-header">
          <div class="section-title-wrap">
            <h3 class="section-title">
              {{
                showStatusInfo.isValid
                  ? "Select Seats to Admit"
                  : "Seats on Ticket"
              }}
            </h3>
            <span
              class="seat-count-pill"
              :class="{ 'pill-locked': !showStatusInfo.isValid }"
              v-if="!bookingDetails.isPrivateShow"
            >
              {{
                showStatusInfo.isValid
                  ? `${selectedSeatCount} of ${seats.length} Selected`
                  : `${seats.length} Seats on Ticket`
              }}
            </span>
            <span class="seat-count-pill pill-private" v-else>
              Private Hall
            </span>
          </div>

          <!-- TICKET TYPES BREAKDOWN -->
          <div
            class="ticket-types-breakdown"
            v-if="
              bookingDetails.arrayOfTicketTypes &&
                bookingDetails.arrayOfTicketTypes.length > 0
            "
          >
            <span
              v-for="(singleSeat, idx) in bookingDetails.arrayOfTicketTypes"
              :key="idx"
              class="tt-pill"
            >
              {{ singleSeat.count }}x {{ singleSeat.tt_name }}
            </span>
          </div>
        </div>

        <!-- SEAT BUTTONS SELECTOR (STANDARD SHOW) -->
        <div class="seat-selector-body" v-if="!bookingDetails.isPrivateShow">
          <div class="seat-chips-grid">
            <button
              @click="onSelectValue('All')"
              :class="{
                'chip-selected':
                  selectedValue === 'All' && showStatusInfo.isValid,
                'chip-disabled': !showStatusInfo.isValid
              }"
              class="seat-chip chip-all"
              :disabled="!showStatusInfo.isValid"
            >
              All
            </button>

            <button
              v-for="(singleSeat, loopKey) in seats"
              :key="loopKey"
              @click="onSelectValue(singleSeat)"
              :class="{
                'chip-disabled': singleSeat.disabled || !showStatusInfo.isValid,
                'chip-selected':
                  singleSeat.selected === true && showStatusInfo.isValid
              }"
              class="seat-chip"
              :disabled="singleSeat.disabled || !showStatusInfo.isValid"
            >
              <span class="seat-label-text">{{
                getSeatLabel(singleSeat)
              }}</span>
              <svg
                v-if="singleSeat.selected && showStatusInfo.isValid"
                class="chip-check-icon"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </button>
          </div>
        </div>

        <!-- PRIVATE SHOW ADMIT STEPPER -->
        <div
          v-if="bookingDetails.isPrivateShow === true"
          class="private-show-counter-wrap"
        >
          <div class="private-info-note">
            Entire Auditorium Booked • Maximum Admits:
            <strong>{{ remainingAdmits }}</strong>
          </div>

          <div class="stepper-controls">
            <button
              class="stepper-btn btn-dec"
              @click="handleAdmitChange('sub')"
              :disabled="currentAdmits <= 0 || !showStatusInfo.isValid"
            >
              -
            </button>

            <div class="stepper-input-wrap">
              <input
                type="number"
                v-model.number="currentAdmits"
                :min="0"
                :max="remainingAdmits"
                class="stepper-input"
                @change="handleInputChange"
                :disabled="!showStatusInfo.isValid"
              />
              <span class="stepper-caption">Guests Admitted</span>
            </div>

            <button
              class="stepper-btn btn-inc"
              @click="handleAdmitChange('add')"
              :disabled="
                currentAdmits >= remainingAdmits || !showStatusInfo.isValid
              "
            >
              +
            </button>
          </div>
        </div>
      </section>

      <!-- 4DX AUDITORIUM SPECIAL POLICIES (IF 4DX) -->
      <section
        class="card policies-card"
        v-if="
          bookingDetails &&
            bookingDetails.mf_name &&
            bookingDetails.mf_name.toLowerCase().includes('4dx')
        "
      >
        <div class="policy-header">
          <img src="@/assets/img/4dx.svg" height="18" alt="4DX" />
          <span>4DX Auditorium Safety Rules</span>
        </div>

        <div class="policy-item">
          <img src="@/assets/img/women.svg" alt="Pregnant" width="24" />
          <p>
            Pregnant women are
            <strong class="text-danger">not allowed</strong> in 4DX.
          </p>
        </div>

        <div class="policy-item">
          <img src="@/assets/img/food.svg" alt="Food & Drink" width="24" />
          <p>
            Outside Food &amp; Beverage are
            <strong class="text-danger">strictly prohibited</strong> in 4DX.
          </p>
        </div>
      </section>
    </div>

    <!-- ============================================================ -->
    <!-- FIXED BOTTOM DOCKED CHECK-IN BAR (NO SCROLLING NEEDED!) -->
    <!-- ============================================================ -->
    <div class="fixed-checkin-dock" v-if="!isLoading">
      <div class="dock-inner">
        <!-- HELPER WARNING NOTE IF TICKET CANNOT BE CHECKED IN -->
        <div
          class="dock-warning-note"
          v-if="!showStatusInfo.isValid"
          :class="{ 'dock-warning-late': showStatusInfo.code === 'LATE_ENTRY' }"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span v-if="showStatusInfo.code === 'LATE_ENTRY'">
            Entry cutoff passed — Movie is currently playing until
            {{ showEndTimeText || "end of show" }}
          </span>
          <span v-else-if="showStatusInfo.code === 'ENTRY_NOT_STARTED'">
            Entry not started yet — Operator permission can admit
          </span>
          <span v-else>{{ showStatusInfo.message }} — Check-In Disabled</span>
          <button class="dock-view-err-link" @click="openValidationPopup">
            View
          </button>
        </div>

        <!-- OPERATOR ALLOW ENTRY DOCKED BUTTON FOR LATE ARRIVAL OR EARLY ENTRY -->
        <button
          v-if="
            (showStatusInfo.code === 'LATE_ENTRY' ||
              showStatusInfo.code === 'ENTRY_NOT_STARTED') &&
              !isLateEntryAllowedByOperator &&
              !isEarlyEntryAllowedByOperator
          "
          id="btn-allow-late"
          @click="allowEntryOverride"
          class="dock-checkin-btn btn-allow-late"
          type="button"
          :title="
            showStatusInfo.code === 'ENTRY_NOT_STARTED'
              ? 'Click to allow early entry'
              : 'Click to allow late entry'
          "
        >
          <svg
            class="dock-btn-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
          </svg>
          <span class="dock-btn-title">
            {{
              showStatusInfo.code === "ENTRY_NOT_STARTED"
                ? "ALLOW ENTRY (EARLY ARRIVAL)"
                : "ALLOW ENTRY (LATE ARRIVAL)"
            }}
          </span>
        </button>

        <!-- MAIN CHECK-IN BUTTON (STICKY, HUGE, THUMB-FRIENDLY, NO SCROLLING!) -->
        <button
          v-else
          id="btn-chg"
          @click="handleCheckInClick"
          class="dock-checkin-btn"
          :class="{
            'btn-disabled': !canCheckIn,
            'btn-checked': isCheckedIn,
            'btn-locked': !showStatusInfo.isValid
          }"
          :disabled="!canCheckIn"
        >
          <!-- LOCKED / NOT PERMITTED ICON -->
          <svg
            v-if="!showStatusInfo.isValid"
            class="dock-btn-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>

          <!-- VALID CHECK-IN ICON -->
          <svg
            v-else-if="!isCheckedIn"
            class="dock-btn-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>

          <!-- CHECKED IN ICON -->
          <svg
            v-else
            class="dock-btn-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>

          <span class="dock-btn-title">
            <template v-if="!showStatusInfo.isValid">
              CHECK-IN NOT ALLOWED
            </template>
            <template v-else-if="isCheckedIn">
              CHECKED-IN
            </template>
            <template v-else>
              CHECK-IN
            </template>
          </span>

          <span
            class="dock-btn-counter"
            v-if="
              showStatusInfo.isValid && !isCheckedIn && selectedSeatCountText
            "
          >
            ({{ selectedSeatCountText }})
          </span>
        </button>
      </div>
    </div>

    <!-- MODERN FIXED BOTTOM NAVIGATION BAR -->
    <footer class="details-bottom-bar">
      <div class="bottom-bar-inner">
        <button
          class="nav-tab-btn"
          @click="goToShow"
          title="View Show Schedule"
        >
          <div class="nav-icon-wrap">
            <img
              src="@/assets/img/reel.svg"
              alt="Shows"
              width="20"
              height="20"
            />
          </div>
          <span class="nav-tab-label">Shows</span>
        </button>

        <button
          class="nav-scanner-center-btn"
          @click="goHome"
          title="Scan Next QR Code"
        >
          <div class="scanner-btn-inner">
            <img
              src="@/assets/img/scanner.svg"
              alt="Scanner"
              width="22"
              height="22"
            />
          </div>
          <span class="nav-tab-label">Scan Next</span>
        </button>

        <button class="nav-tab-btn" @click="onLogout" title="Logout">
          <div class="nav-icon-wrap">
            <img
              src="@/assets/img/logout.svg"
              alt="Logout"
              width="20"
              height="20"
            />
          </div>
          <span class="nav-tab-label">Logout</span>
        </button>
      </div>
    </footer>

    <!-- ============================================================ -->
    <!-- NEW VALIDATION & ERROR POPUP MODAL (AS REQUESTED) -->
    <!-- ============================================================ -->
    <b-modal
      id="validationModal"
      ref="validationModal"
      centered
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      modal-class="modern-validation-modal-wrapper"
    >
      <div class="val-modal-box">
        <!-- ICON WRAPPER -->
        <div
          class="val-modal-icon-wrap"
          :class="'val-icon-' + validationError.code.toLowerCase()"
        >
          <!-- CLOCK ICON: ENTRY NOT STARTED -->
          <svg
            v-if="validationError.code === 'ENTRY_NOT_STARTED'"
            class="val-svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>

          <!-- LATE ARRIVAL / SHOW IN PROGRESS -->
          <svg
            v-else-if="validationError.code === 'LATE_ENTRY'"
            class="val-svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
            <path d="M19 12h-3"></path>
          </svg>

          <!-- WARNING / HISTORY ICON: SHOW PASSED -->
          <svg
            v-else-if="validationError.code === 'SHOW_PASSED'"
            class="val-svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>

          <!-- LOCATION MISMATCH ICON: WRONG CINEMA -->
          <svg
            v-else-if="validationError.code === 'CINEMA_MISMATCH'"
            class="val-svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
            <line x1="3" y1="3" x2="21" y2="21" stroke-width="2.5"></line>
          </svg>

          <!-- ALREADY SCANNED ICON -->
          <svg
            v-else-if="validationError.code === 'ALREADY_SCANNED'"
            class="val-svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>

          <!-- DEFAULT ERROR ICON -->
          <svg
            v-else
            class="val-svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>

        <!-- MODAL TITLE & NOTICE -->
        <div class="val-modal-head">
          <div
            class="val-type-pill"
            :class="'pill-' + validationError.code.toLowerCase()"
          >
            {{ validationError.title }}
          </div>
          <h3 class="val-primary-title">
            {{ validationError.message }}
          </h3>
          <p class="val-submessage" v-if="validationError.subMessage">
            {{ validationError.subMessage }}
          </p>
        </div>

        <!-- MOVIE DETAILS CARD INSIDE POPUP -->
        <div
          class="val-movie-card"
          v-if="
            bookingDetails &&
              (bookingDetails.movie_title || bookingDetails.movie_name)
          "
        >
          <div class="val-movie-top-bar">
            <h4 class="val-movie-name">
              {{ bookingDetails.movie_title || bookingDetails.movie_name }}
            </h4>
            <div class="val-chips">
              <span
                class="val-chip val-chip-lang"
                v-if="
                  bookingDetails.lang_version ||
                    bookingDetails.lang_name ||
                    bookingDetails.version_name
                "
              >
                {{
                  bookingDetails.lang_version ||
                    bookingDetails.lang_name ||
                    bookingDetails.version_name
                }}
              </span>
              <span
                class="val-chip val-chip-format"
                v-if="bookingDetails.mf_name"
              >
                {{ bookingDetails.mf_name }}
              </span>
            </div>
          </div>

          <div class="val-grid">
            <!-- CINEMA NAME -->
            <div class="val-grid-row">
              <span class="val-label">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M3 21h18M3 7v14M21 7v14M6 7V3h12v4M9 11h2M9 15h2M13 11h2M13 15h2"
                  ></path>
                </svg>
                Cinema:
              </span>
              <span
                class="val-data"
                :class="{
                  'val-cinema-alert': validationError.code === 'CINEMA_MISMATCH'
                }"
              >
                {{
                  bookingDetails.cine_name || bookingDetails.cinema_name || "-"
                }}
              </span>
            </div>

            <!-- SCREEN NAME -->
            <div class="val-grid-row">
              <span class="val-label">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
                Screen:
              </span>
              <span class="val-data">
                {{ bookingDetails.screen_name || "-" }}
              </span>
            </div>

            <!-- SHOW TIME -->
            <div class="val-grid-row">
              <span class="val-label">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Show Time:
              </span>
              <span class="val-data val-time-data">
                <strong>{{ bookingDetails.ss_start_show_time || "-" }}</strong>
                <small class="val-date-sub" v-if="bookingDetails.showdate">
                  ({{ bookingDetails.showdate }})
                </small>
              </span>
            </div>

            <!-- LANGUAGE -->
            <div class="val-grid-row">
              <span class="val-label">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path
                    d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                  ></path>
                </svg>
                Language:
              </span>
              <span class="val-data">
                {{
                  bookingDetails.lang_version ||
                    bookingDetails.lang_name ||
                    bookingDetails.version_name ||
                    "-"
                }}
              </span>
            </div>

            <!-- SEAT NAME -->
            <div class="val-grid-row val-row-seats">
              <span class="val-label">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M4 18v3M20 18v3M4 10a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2M6 14h12v4H6z"
                  ></path>
                </svg>
                Seats:
              </span>
              <span class="val-data val-seats-badge">
                {{ computedSeatNames }}
              </span>
            </div>
          </div>
        </div>

        <!-- MODAL BUTTON ACTIONS -->
        <div class="val-actions-box">
          <!-- PRIMARY ACTION BUTTON FOR OPERATOR: ALLOW ENTRY (IF LATE ARRIVAL OR EARLY ENTRY) -->
          <button
            v-if="
              validationError.code === 'LATE_ENTRY' ||
                validationError.code === 'ENTRY_NOT_STARTED'
            "
            class="val-action-btn-allow"
            @click="allowEntryOverride"
            type="button"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Allow Entry (Operator Override)</span>
          </button>

          <button class="val-action-btn-primary" @click="gotoHome">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span>Scan Another QR Code</span>
          </button>

          <button
            class="val-action-btn-secondary"
            @click="dismissValidationModal"
          >
            Dismiss &amp; View Details
          </button>
        </div>
      </div>
    </b-modal>

    <!-- OTHER EXISTING APPLICATION MODALS (PRESERVED & STYLED) -->

    <!-- CHECK-IN CONFIRMATION MODAL -->
    <b-modal id="modal-1" ref="modal1" centered hide-footer hide-header>
      <div class="text-center py-4 modern-modal-padding">
        <div class="mb-3">
          <img
            src="@/assets/img/check.svg"
            height="85"
            width="85"
            alt="Success"
          />
        </div>

        <h4 class="font-weight-bold">Check-In Successful</h4>
        <p class="text-muted">How many people are entering now?</p>

        <div class="seat-chips-grid my-3" v-if="!bookingDetails.isPrivateShow">
          <button
            @click="onSelectValue('All')"
            :class="selectedValue == 'All' ? 'chip-selected' : ''"
            class="seat-chip chip-all"
          >
            All
          </button>

          <button
            v-for="(singleSeat, loopKey) in seats"
            :key="loopKey"
            @click="onSelectValue(singleSeat)"
            :class="{
              'chip-disabled': singleSeat.disabled,
              'chip-selected': singleSeat.selected == true
            }"
            class="seat-chip"
          >
            {{ getSeatLabel(singleSeat) }}
          </button>
        </div>

        <div
          v-if="bookingDetails.isPrivateShow === true"
          class="stepper-controls my-4"
        >
          <button class="stepper-btn btn-dec" @click="handleAdmitChange('sub')">
            -
          </button>
          <input
            type="number"
            v-model.number="currentAdmits"
            :min="0"
            :max="remainingAdmits"
            class="stepper-input"
            @change="handleInputChange"
          />
          <button class="stepper-btn btn-inc" @click="handleAdmitChange('add')">
            +
          </button>
        </div>

        <button
          @click="
            isMifClient ? showPrintOption(true) : checkSubmitAvailable(false)
          "
          class="dock-checkin-btn w-100 mt-3"
          :class="{
            'btn-disabled':
              (!bookingDetails.isPrivateShow && selectedValue.length == 0) ||
              disableConfirmed == true ||
              (bookingDetails.isPrivateShow === true && currentAdmits == 0)
          }"
        >
          Confirm Entry
        </button>

        <button @click="goHome()" class="val-action-btn-secondary w-100 mt-2">
          Back to Scanner
        </button>
      </div>
    </b-modal>

    <!-- GATE RESTRICTION WARNING MODAL -->
    <b-modal id="EntryMsg" ref="EntryMsg" centered hide-footer hide-header>
      <div class="text-center py-4 modern-modal-padding">
        <div class="mb-3">
          <img
            src="@/assets/img/warning-icon.svg"
            height="65"
            width="65"
            alt="Warning"
          />
        </div>

        <h5 class="font-weight-bold text-warning">Entry Gate Warning</h5>
        <p class="text-danger font-weight-bold">
          Entry From this Gate is Not Allowed for Some Seats!
        </p>

        <table class="table border modern-table" border="1">
          <thead>
            <tr>
              <th scope="col">Seat</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(eSeat, eSeatIndex) in ErrorSeatsComputed"
              :key="eSeatIndex"
            >
              <td class="font-weight-bold">
                <div class="d-flex flex-wrap" style="word-break: break-word">
                  {{ eSeat[1].map(x => x.sl_seat_name).join(", ") }}
                </div>
              </td>
              <td>{{ eSeat[1][0].entry_description || "-" }}</td>
            </tr>
          </tbody>
        </table>

        <button
          @click="onSubmitSeats(privateShowprops)"
          class="dock-checkin-btn w-75 mx-auto mt-3"
        >
          Confirm Remaining Seats
        </button>
      </div>
    </b-modal>

    <!-- NO ENTRY FROM GATE MODAL -->
    <b-modal
      id="noEntryAvailable"
      ref="noEntryAvailable"
      centered
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="text-center py-4 modern-modal-padding">
        <div class="mb-3">
          <img
            src="@/assets/img/warning-icon.svg"
            height="65"
            width="65"
            alt="Warning"
          />
        </div>

        <h5 class="font-weight-bold text-danger">Access Denied</h5>
        <p class="text-danger font-weight-bold">
          Entries Not Available From this Gate!
        </p>

        <div class="alert alert-light">
          {{
            ErrorSeats &&
              ErrorSeats.length > 0 &&
              ErrorSeats[0].entry_description
          }}
        </div>

        <button @click="gotoHome()" class="dock-checkin-btn w-75 mx-auto mt-3">
          Back to Scanner
        </button>
      </div>
    </b-modal>

    <!-- TICKET PRINT CONFIRMATION MODAL -->
    <b-modal
      id="ticketPrintConfirmation"
      ref="ticketPrintConfirmation"
      centered
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      no-fade
    >
      <div class="text-center py-4 modern-modal-padding">
        <div class="mb-3">
          <img
            src="@/assets/img/printer.svg"
            height="55"
            width="55"
            alt="Printer"
          />
        </div>
        <h5 class="font-weight-bold">Print Ticket?</h5>
        <p class="text-muted">Would you like to print physical tickets now?</p>

        <div class="d-flex justify-content-center mt-3" style="gap: 12px">
          <button
            @click="checkSubmitAvailable(false, true)"
            class="dock-checkin-btn px-4"
          >
            Yes, Print
          </button>
          <button
            @click="checkSubmitAvailable(false, false)"
            class="val-action-btn-secondary px-4 m-0"
          >
            No, Skip
          </button>
        </div>
      </div>
    </b-modal>

    <!-- SUCCESS MSG MODAL -->
    <b-modal
      id="successMsgModal"
      ref="successMsgModal"
      centered
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="text-center py-4 modern-modal-padding">
        <div class="mb-3">
          <img
            src="@/assets/img/check.svg"
            height="75"
            width="75"
            alt="Success"
          />
        </div>
        <h4 class="font-weight-bold text-success">Check-In Successful</h4>
        <p class="font-weight-bold mt-2">{{ successModalMessage }}</p>
        <button
          @click="closeSuccessModal"
          class="dock-checkin-btn w-75 mx-auto mt-3"
        >
          Close &amp; Scan Next
        </button>
      </div>
    </b-modal>

    <!-- HIDDEN CONTAINER FOR PRINTING -->
    <div class="d-none" ref="print_boxoffice_fnb_ticket">
      <div v-html="EjsData"></div>
    </div>

    <!-- GENERIC ERROR MODAL (FALLBACK) -->
    <ErrorModal
      v-model="isErrorModalVisible"
      :title="errorModalMessage"
      size="lg"
      :close-on-backdrop="false"
    >
    </ErrorModal>
  </div>
</template>

<script>
import BookingService from "@/services/BookingService";
import { URL_BASE as BASEURL } from "@/helper/constants";
import constant from "@/helper/constant";
import { getLangSpecificAttribute } from "@/helper/Languages";
import _ from "lodash";
import ErrorModal from "../components/errorModal.vue";
import Printd from "printd";
import moment from "moment";

export default {
  name: "TicketDetails",
  components: {
    ErrorModal
  },
  data() {
    return {
      EjsData: null,
      isErrorModalVisible: false,
      errorModalTitle: "Error",
      errorModalMessage: "",
      isQfxCinema: "",
      bookingDetails: {},
      seat: "",
      seats: [],
      qrcode: "",
      selectedValue: "All",
      currencyData: [],
      disableConfirmed: false,
      isCheckedIn: false,
      MovieContentArtworks: [],
      currentAdmits: 0,
      remainingAdmits: 0,
      isLoading: false,
      noEntryAvailable: false,
      someEntrynotAvailable: false,
      ErrorSeats: [],
      privateShowprops: false,
      successModalMessage: "",
      successRedirectTimeout: null,

      // NEW VALIDATION AND ERROR POPUP DATA
      isValidationPopupVisible: false,
      isLateEntryAllowedByOperator: false,
      isEarlyEntryAllowedByOperator: false,
      validationError: {
        code: "", // 'ENTRY_NOT_STARTED' | 'SHOW_PASSED' | 'LATE_ENTRY' | 'CINEMA_MISMATCH' | 'API_ERROR'
        title: "",
        message: "",
        subMessage: ""
      },
      showStatusInfo: {
        isValid: true,
        code: "VALID",
        message: "Valid Entry",
        subMessage: "Ticket verified. Ready for check-in."
      }
    };
  },
  computed: {
    showEndTimeText() {
      if (!this.bookingDetails) return "";
      const b = this.bookingDetails;

      if (b.ss_actual_end_date_time) {
        const m = moment(b.ss_actual_end_date_time);
        if (m.isValid()) return m.format("hh:mm A");
      }

      if (b.ss_end_show_time) {
        const m = moment(b.ss_end_show_time, [
          "HH:mm:ss",
          "HH:mm",
          "hh:mm A",
          "hh:mm:ss A"
        ]);
        if (m.isValid()) return m.format("hh:mm A");
        return b.ss_end_show_time;
      }

      const startTime = b.ss_start_show_time;
      if (startTime) {
        const startDate = b.ss_actual_start_date || b.ss_start_date;
        let startDateStr = startDate
          ? startDate.includes("T")
            ? startDate.split("T")[0]
            : startDate.split(" ")[0]
          : moment().format("YYYY-MM-DD");
        const showStart = moment(`${startDateStr} ${startTime}`, [
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DD HH:mm",
          "YYYY-MM-DD hh:mm A",
          "YYYY-MM-DD hh:mm:ss A"
        ]);
        if (showStart.isValid()) {
          let durationMins = 120;
          const rawDur =
            b.movie_duration || b.duration || b.md_duration || b.run_time;
          if (rawDur) {
            const match = String(rawDur).match(/\d+/);
            if (match) durationMins = parseInt(match[0], 10);
          }
          return moment(showStart)
            .add(durationMins, "minutes")
            .format("hh:mm A");
        }
      }

      return "";
    },
    computedSeatNames() {
      if (this.bookingDetails && this.bookingDetails.isPrivateShow) {
        return "Entire Hall Booked";
      }
      if (this.seats && this.seats.length > 0) {
        return this.seats.map(s => this.getSeatLabel(s)).join(", ");
      }
      if (this.bookingDetails && this.bookingDetails.seats) {
        if (Array.isArray(this.bookingDetails.seats)) {
          return this.bookingDetails.seats.join(", ");
        }
        return String(this.bookingDetails.seats);
      }
      if (
        this.bookingDetails &&
        this.bookingDetails.seatArrayForQrCode &&
        this.bookingDetails.seatArrayForQrCode.length > 0
      ) {
        return this.bookingDetails.seatArrayForQrCode
          .map(s => this.getSeatLabel(s))
          .join(", ");
      }
      if (
        this.bookingDetails &&
        this.bookingDetails.arrayOfTicketTypes &&
        this.bookingDetails.arrayOfTicketTypes.length > 0
      ) {
        return this.bookingDetails.arrayOfTicketTypes
          .map(t => `${t.count} ${t.tt_name}`)
          .join(", ");
      }
      return "-";
    },
    selectedSeatCount() {
      if (this.bookingDetails && this.bookingDetails.isPrivateShow) {
        return this.currentAdmits;
      }
      if (this.selectedValue === "All") {
        return this.seats.filter(s => !s.disabled).length;
      }
      return this.seats.filter(s => s.selected && !s.disabled).length;
    },
    selectedSeatCountText() {
      if (this.bookingDetails && this.bookingDetails.isPrivateShow) {
        return this.currentAdmits > 0 ? `${this.currentAdmits} Admits` : "";
      }
      const count = this.selectedSeatCount;
      if (count <= 0) return "";
      return count === 1 ? "1 Seat" : `${count} Seats`;
    },
    canCheckIn() {
      if (!this.showStatusInfo.isValid) return false;
      if (this.disableConfirmed) return false;
      if (this.bookingDetails && this.bookingDetails.isPrivateShow === true) {
        return this.currentAdmits > 0;
      }
      return this.selectedSeatCount > 0;
    },
    ErrorSeatsComputed() {
      let tempArr = [];
      if (this.ErrorSeats.length > 0) {
        tempArr = _.toPairs(
          _.groupBy(this.ErrorSeats.filter(x => x), "entry_id")
        );
      }
      return tempArr;
    },
    isMifClient() {
      const client = window.localStorage.getItem("CLIENT_NAME");
      return client ? client.toLowerCase() === "mif" : false;
    },
    isNovo01Mall() {
      const client = (
        window.localStorage.getItem("CLIENT_NAME") || ""
      ).toLowerCase();
      const cinemaId = Number(
        this.bookingDetails.cine_id || this.bookingDetails.cinema_id
      );
      const cinemaName = (this.bookingDetails.cine_name || "")
        .trim()
        .toLowerCase();

      return (
        client === "novocinemas" && cinemaId === 3 && cinemaName === "01 mall"
      );
    },
    movieRating() {
      if (!this.bookingDetails) return "";
      const b = this.bookingDetails;
      const candidates = [
        b.rating,
        b.censor,
        b.censor_rating,
        b.movie_rating,
        b.certificate
      ];
      for (const val of candidates) {
        if (val !== undefined && val !== null && String(val).trim() !== "") {
          return String(val).trim();
        }
      }
      if (
        b.age_limit !== undefined &&
        b.age_limit !== null &&
        String(b.age_limit).trim() !== ""
      ) {
        return b.age_limit;
      }
      return "";
    },
    showAgeLabel() {
      if (!this.bookingDetails) return false;
      const b = this.bookingDetails;
      const hasRating = [
        b.rating,
        b.censor,
        b.censor_rating,
        b.movie_rating,
        b.certificate
      ].some(
        val => val !== undefined && val !== null && String(val).trim() !== ""
      );

      if (
        !hasRating &&
        b.age_limit !== undefined &&
        b.age_limit !== null &&
        String(b.age_limit).trim() !== ""
      ) {
        return true;
      }
      return false;
    }
  },
  beforeMount() {
    let code = String(this.$route.query.qrcode);
    if (code.includes("http")) {
      code = code.split("/");
      this.qrcode = code[code.length - 1];
    } else {
      this.qrcode = code;
    }

    if (this.qrcode) {
      this.getBookingDetails(this.qrcode);
    } else {
      this.$router.go(-1);
    }
  },
  mounted() {
    this._GetGlobalOptions().then(() => {
      this.getCurrencyCode();
    });
  },
  beforeDestroy() {
    clearTimeout(this.successRedirectTimeout);
  },
  methods: {
    getLangSpecificAttribute,
    goToShow() {
      this.$router.push({
        name: "showtime",
        path: "/showtime"
      });
    },
    goHome() {
      this.$router.push({
        name: "qrCode"
      });
    },
    gotoHome() {
      this.dismissValidationModal();
      this.$router.push({
        name: "qrCode"
      });
    },
    onLogout() {
      window.localStorage.removeItem("JWT_TOKEN");
      window.localStorage.removeItem("USERCINEINFO");
      window.localStorage.clear();
      this.$cookie.delete("BRHSID");
      this.$router.push({
        name: "login"
      });
      return window.caches.keys();
    },

    // ============================================================
    // VALIDATION LOGIC FOR ERROR MESSAGES & POPUP
    // ============================================================
    validateBooking(booking) {
      if (!booking) {
        return {
          isValid: false,
          code: "API_ERROR",
          title: "Booking Not Found",
          message: "No booking details found for this QR code.",
          subMessage: "Please verify the QR code and scan again."
        };
      }

      // 1. DIFFERENT CINEMA CHECK:
      const loggedInCineId = window.localStorage.getItem("USERCINEINFO");
      const ticketCineId = booking.cine_id || booking.cinema_id;
      const ticketCinemaName = booking.cine_name || booking.cinema_name || "";

      if (
        booking.cinemaMismatch === true ||
        (loggedInCineId &&
          ticketCineId &&
          String(loggedInCineId).trim() !== String(ticketCineId).trim())
      ) {
        return {
          isValid: false,
          code: "CINEMA_MISMATCH",
          title: "Cinema Mismatch",
          message: "This QR/Booking Code does not belong to this cinema",
          subMessage: ticketCinemaName
            ? `Ticket booked at "${ticketCinemaName}". Please proceed to the correct cinema location.`
            : "This QR/Booking Code belongs to another cinema branch."
        };
      }

      // 2. SHOW TIME & DATE VALIDATION (ENTRY WINDOW):
      const startDate =
        booking.ss_actual_start_date ||
        booking.ss_start_date ||
        booking.show_date ||
        booking.showdate;
      const startTime = booking.ss_start_show_time;

      if (startDate && startTime) {
        let startDateStr = startDate;
        if (typeof startDateStr === "string") {
          if (startDateStr.includes("T")) {
            startDateStr = startDateStr.split("T")[0];
          } else if (startDateStr.includes(" ")) {
            startDateStr = startDateStr.split(" ")[0];
          }
        } else if (startDateStr) {
          startDateStr = moment(startDateStr).format("YYYY-MM-DD");
        }

        const showStart = moment(`${startDateStr} ${startTime}`, [
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DD HH:mm",
          "YYYY-MM-DD hh:mm A",
          "YYYY-MM-DD hh:mm:ss A",
          "YYYY-MM-DD h:mm A",
          "YYYY-MM-DD h:mm"
        ]);

        if (showStart.isValid()) {
          const now = moment();

          // 2A. CHECK ENTRY BEFORE SHOW START:
          let beforeMinutes = 30; // default 30 min
          const rawBefore =
            booking.entry_before_show_start !== undefined &&
            booking.entry_before_show_start !== null
              ? booking.entry_before_show_start
              : booking.entry_start_before_min !== undefined &&
                booking.entry_start_before_min !== null
              ? booking.entry_start_before_min
              : booking.entry_before_min;

          if (
            rawBefore !== undefined &&
            rawBefore !== null &&
            String(rawBefore).trim() !== ""
          ) {
            const matchBefore = String(rawBefore).match(/\d+/);
            if (matchBefore) {
              beforeMinutes = parseInt(matchBefore[0], 10);
            }
          }

          const entryAllowedFrom = moment(showStart).subtract(
            beforeMinutes,
            "minutes"
          );

          // 2B. CHECK ENTRY AFTER SHOW START (CUTOFF):
          let afterMinutes = null;
          const rawAfter =
            booking.entry_after_show_start !== undefined &&
            booking.entry_after_show_start !== null
              ? booking.entry_after_show_start
              : booking.entry_after_min !== undefined &&
                booking.entry_after_min !== null
              ? booking.entry_after_min
              : booking.entry_closed_after_min;

          if (
            rawAfter !== undefined &&
            rawAfter !== null &&
            String(rawAfter).trim() !== ""
          ) {
            const matchAfter = String(rawAfter).match(/\d+/);
            if (matchAfter) {
              afterMinutes = parseInt(matchAfter[0], 10);
            }
          }

          // Determine when the show actually ends:
          let showEnd = null;
          if (booking.ss_actual_end_date_time) {
            const mEnd = moment(booking.ss_actual_end_date_time);
            if (mEnd.isValid()) showEnd = mEnd;
          }
          if (!showEnd && booking.ss_end_show_time) {
            const endDate =
              booking.ss_actual_end_date || booking.ss_end_date || startDate;
            let endDateStr = endDate.includes("T")
              ? endDate.split("T")[0]
              : endDate.includes(" ")
              ? endDate.split(" ")[0]
              : endDate;
            let mEnd = moment(`${endDateStr} ${booking.ss_end_show_time}`, [
              "YYYY-MM-DD HH:mm:ss",
              "YYYY-MM-DD HH:mm",
              "YYYY-MM-DD hh:mm A",
              "YYYY-MM-DD hh:mm:ss A"
            ]);
            if (mEnd.isValid()) {
              if (mEnd.isBefore(showStart)) {
                mEnd = mEnd.add(1, "days");
              }
              showEnd = mEnd;
            }
          }
          if (!showEnd) {
            let durationMins = 120;
            const rawDur =
              booking.movie_duration ||
              booking.duration ||
              booking.md_duration ||
              booking.run_time;
            if (rawDur) {
              const match = String(rawDur).match(/\d+/);
              if (match) durationMins = parseInt(match[0], 10);
            }
            showEnd = moment(showStart).add(durationMins, "minutes");
          }

          // Entry cutoff time (defaults to 30 mins after show start):
          let entryCutoff = null;
          if (afterMinutes !== null) {
            entryCutoff = moment(showStart).add(afterMinutes, "minutes");
          } else {
            entryCutoff = moment(showStart).add(30, "minutes");
          }

          // 1. EVALUATE SHOW PASSED (Show has actually ended):
          const isShowActuallyEnded =
            booking.showEnded === true ||
            now.isAfter(showEnd) ||
            (booking.ss_actual_end_date_time &&
              now.isAfter(moment(booking.ss_actual_end_date_time)));

          if (isShowActuallyEnded) {
            return {
              isValid: false,
              code: "SHOW_PASSED",
              title: "Show Ended",
              message: "Show Has Ended",
              subMessage: `The show was scheduled for ${showStart.format(
                "hh:mm A, ddd DD MMM"
              )} and ended at ${showEnd.format("hh:mm A")}. Entry is closed.`
            };
          }

          // 2. EVALUATE ENTRY NOT STARTED:
          // Strictly check if current device time is before entry opening time.
          // Do NOT let backend's stale/UTC booking.entryStarted flag override actual schedule!
          if (now.isBefore(entryAllowedFrom)) {
            if (
              this.isEarlyEntryAllowedByOperator ||
              this.isLateEntryAllowedByOperator
            ) {
              return {
                isValid: true,
                code: "EARLY_ENTRY_ALLOWED",
                title: "Early Entry Approved",
                message: "Early Entry Allowed by Operator",
                subMessage: `Show starts at ${showStart.format(
                  "hh:mm A"
                )}. Operator authorized early entry.`
              };
            }

            const isTomorrowOrFuture = showStart
              .clone()
              .startOf("day")
              .isAfter(now.clone().startOf("day"));

            let subMsg = "";
            if (isTomorrowOrFuture) {
              subMsg = `Show is scheduled for ${showStart.format(
                "ddd, DD MMM YYYY"
              )} at ${showStart.format(
                "hh:mm A"
              )}. Entry opens ${beforeMinutes} minutes prior.`;
            } else {
              subMsg = `Show starts at ${showStart.format(
                "hh:mm A"
              )}. Entry opens at ${entryAllowedFrom.format(
                "hh:mm A"
              )} (${beforeMinutes} mins before showtime).`;
            }

            return {
              isValid: false,
              code: "ENTRY_NOT_STARTED",
              title: "Entry Not Started",
              message: "Entry not started yet",
              subMessage: subMsg
            };
          }

          // 3. EVALUATE LATE ARRIVAL (Cutoff time passed, BUT movie is STILL playing!):
          const isCutoffPassed = entryCutoff ? now.isAfter(entryCutoff) : false;
          const isLateArrival =
            isCutoffPassed ||
            (booking.entryClosed === true && now.isAfter(showStart));

          if (isLateArrival) {
            if (
              this.isLateEntryAllowedByOperator ||
              this.isEarlyEntryAllowedByOperator
            ) {
              return {
                isValid: true,
                code: "LATE_ENTRY_ALLOWED",
                title: "Late Entry Approved",
                message: "Late Entry Allowed by Operator",
                subMessage: `Movie is currently playing until ${showEnd.format(
                  "hh:mm A"
                )}. Operator authorized entry.`
              };
            }

            return {
              isValid: false,
              code: "LATE_ENTRY",
              title: "Late Arrival — Show In Progress",
              message: "Entry Cutoff Time Expired",
              subMessage: `Show started at ${showStart.format(
                "hh:mm A"
              )}. Standard entry closed at ${entryCutoff.format(
                "hh:mm A"
              )}, but the show is still playing until ${showEnd.format(
                "hh:mm A"
              )}. Operator permission required to admit.`
            };
          }

          // 4. NORMAL VALID ENTRY WINDOW (Between entryAllowedFrom and cutoff):
          if (booking.alreadyScanned === true || booking.allOccupied === true) {
            return {
              isValid: false,
              code: "ALREADY_SCANNED",
              title: "Already Scanned",
              message: "This ticket has already been scanned",
              subMessage:
                "All seats on this booking have already been checked in."
            };
          }

          return {
            isValid: true,
            code: "VALID",
            title: "Valid Entry",
            message: "Valid Entry",
            subMessage: "Ticket verified. Ready for check-in."
          };
        }
      }

      // FALLBACK CHECKS (If schedule details are missing or invalid):
      if (booking.showEnded === true) {
        return {
          isValid: false,
          code: "SHOW_PASSED",
          title: "Show Ended",
          message: "Show Has Ended",
          subMessage: "Entries are closed for this show."
        };
      }

      if (booking.entryStarted === false && !booking.entryClosed) {
        if (
          this.isEarlyEntryAllowedByOperator ||
          this.isLateEntryAllowedByOperator
        ) {
          return {
            isValid: true,
            code: "EARLY_ENTRY_ALLOWED",
            title: "Early Entry Approved",
            message: "Early Entry Allowed by Operator",
            subMessage: "Operator authorized early entry for this show."
          };
        }
        return {
          isValid: false,
          code: "ENTRY_NOT_STARTED",
          title: "Entry Not Started",
          message: "Entry not started yet",
          subMessage: "Entry has not opened for this show yet."
        };
      }

      if (booking.entryClosed === true) {
        if (
          this.isLateEntryAllowedByOperator ||
          this.isEarlyEntryAllowedByOperator
        ) {
          return {
            isValid: true,
            code: "LATE_ENTRY_ALLOWED",
            title: "Late Entry Approved",
            message: "Late Entry Allowed by Operator",
            subMessage: "Operator authorized late entry for this show."
          };
        }
        return {
          isValid: false,
          code: "LATE_ENTRY",
          title: "Late Arrival — Show In Progress",
          message: "Entry Cutoff Time Expired",
          subMessage:
            "Standard entry cutoff has passed, but the show is still running. Operator permission required to admit."
        };
      }

      if (booking.alreadyScanned === true || booking.allOccupied === true) {
        return {
          isValid: false,
          code: "ALREADY_SCANNED",
          title: "Already Scanned",
          message: "This ticket has already been scanned",
          subMessage: "All seats on this booking have already been checked in."
        };
      }

      return {
        isValid: true,
        code: "VALID",
        title: "Valid Entry",
        message: "Valid Entry",
        subMessage: "Ticket verified. Ready for check-in."
      };
    },

    openValidationPopup() {
      this.isValidationPopupVisible = true;
      this.$nextTick(() => {
        if (this.$refs.validationModal) {
          this.$refs.validationModal.show();
        }
      });
    },

    dismissValidationModal() {
      this.isValidationPopupVisible = false;
      if (this.$refs.validationModal) {
        this.$refs.validationModal.hide();
      }
    },

    allowEntryOverride() {
      this.isLateEntryAllowedByOperator = true;
      this.isEarlyEntryAllowedByOperator = true;
      this.dismissValidationModal();

      let showEndStr = this.showEndTimeText || "end of show";
      const isEarly =
        this.validationError &&
        this.validationError.code === "ENTRY_NOT_STARTED";
      this.showStatusInfo = {
        isValid: true,
        code: isEarly ? "EARLY_ENTRY_ALLOWED" : "LATE_ENTRY_ALLOWED",
        title: isEarly ? "Early Entry Approved" : "Late Entry Approved",
        message: isEarly
          ? "Early Entry Allowed by Operator"
          : "Late Entry Allowed by Operator",
        subMessage: isEarly
          ? "Operator authorized early entry for this show."
          : `Movie in progress (playing until ${showEndStr}). Operator authorized entry.`
      };

      // Auto-select valid seats for immediate 1-tap check-in:
      if (!this.bookingDetails.isPrivateShow) {
        this.selectedValue = "All";
        this.seats = this.seats.map(seat => {
          if (!seat.disabled) {
            seat.selected = true;
          }
          return seat;
        });
      } else {
        this.currentAdmits =
          this.remainingAdmits || this.bookingDetails.admits || 1;
      }

      this.$toasted.show("Entry approved by operator. Ready for check-in.", {
        type: "success",
        duration: 3500
      });
    },

    allowLateEntry() {
      this.allowEntryOverride();
    },

    getAgeColor(val) {
      let color = `#FAFAFA`;
      if (val === undefined || val === null || val === "") {
        return color;
      }

      let age = Number(val);
      if (isNaN(age)) {
        const match = String(val).match(/\d+/);
        if (match) {
          age = parseInt(match[0], 10);
        } else {
          const upper = String(val)
            .trim()
            .toUpperCase();
          if (["A", "R", "NC-17", "18TC"].includes(upper)) {
            age = 18;
          } else if (["UA", "PG-13", "PG13"].includes(upper)) {
            age = 13;
          } else if (["PG"].includes(upper)) {
            age = 8;
          } else if (["U", "G"].includes(upper)) {
            age = 0;
          }
        }
      }

      if (!isNaN(age)) {
        if (age < 6) {
          color = "#E0E7FF";
        } else if (age >= 6 && age < 12) {
          color = "#FDC500";
        } else if (age >= 12 && age < 16) {
          color = "#10B981";
        } else if (age >= 16 && age < 18) {
          color = "#009FDB";
        } else if (age >= 18) {
          color = "#E11D48";
        }
      }
      return color;
    },

    handleAdmitChange(type) {
      if (type === "add") {
        if (
          parseInt(this.currentAdmits) + 1 <=
          parseInt(this.remainingAdmits)
        ) {
          this.currentAdmits = parseInt(this.currentAdmits) + 1;
        } else {
          this.$toasted.show("Reached Max Seat Limit In Audi");
        }
      } else if (type === "sub") {
        if (parseInt(this.currentAdmits) - 1 >= 0) {
          this.currentAdmits = parseInt(this.currentAdmits) - 1;
        }
      }
    },

    handleInputChange($event) {
      let num = parseInt($event.target.value);
      if (num < 0) {
        this.$toasted.show("Cannot have negative values");
        this.currentAdmits = 0;
      } else if (num > parseInt(this.remainingAdmits)) {
        this.$toasted.show("Exceeded Seat Limit in Audi");
        this.currentAdmits = 0;
      }
    },

    onSelectValue(value) {
      if (!this.showStatusInfo.isValid) {
        this.openValidationPopup();
        return;
      }

      if (value === "All") {
        this.selectedValue = "All";
        this.seats = this.seats.map(seat => {
          seat.selected = !seat.disabled;
          return seat;
        });
      } else {
        this.selectedValue = "selected";
        this.seats = this.seats.map(seat => {
          if (seat.ssb_id === value.ssb_id && !seat.disabled) {
            seat.selected = !seat.selected;
          }
          return seat;
        });
      }

      let count = 0;
      this.seats.forEach(seat => {
        if (seat.selected === true) {
          count++;
        }
      });
      if (count === 0 && this.selectedValue !== "All") {
        this.selectedValue = "";
      }
    },

    handleCheckInClick() {
      if (!this.showStatusInfo.isValid) {
        this.openValidationPopup();
        return;
      }

      if (this.isMifClient) {
        this.showPrintOption(true);
      } else {
        this.checkSubmitAvailable(true);
      }
    },

    _GetImagePath(imagePath) {
      return imagePath;
    },

    _GetGlobalOptions() {
      return new Promise((resolve, reject) => {
        BookingService.GetGlobalOptions()
          .then(response => {
            const { data } = response;
            if (data && data.status && data.Records.length > 0) {
              data.Records.forEach(x => {
                if (x.go_key === "ORGANIZATION_NAME") {
                  window.localStorage.setItem("isCinemaName", x.go_value);
                }
                if (x.go_key === "CURRENCY") {
                  window.localStorage.setItem("BRGLOPCUR", x.go_value);
                  window.localStorage.setItem("BRGLOPCU", x);
                } else if (x.go_key === "APPLICATION_LANGUAGE") {
                  window.localStorage.setItem("BRGLOPAL", x.go_value);
                } else if (x.go_key === "OPERATION_COUNTRY") {
                  window.localStorage.setItem("BRGLOPCO", x.go_value);
                }
              });
              resolve("data.Records");
            } else {
              reject(data ? data.message : "No records");
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    getCurrencyCode() {
      let payload = {
        tableName: "ms_currencies",
        curr_is_active: "Y",
        curr_id: window.localStorage.getItem("BRGLOPCUR")
      };
      BookingService.GetActiveList(payload)
        .then(response => {
          const { data } = response;
          if (data && data.status && data.Records.length > 0) {
            this.currencyData = data.Records[0];
          }
        })
        .catch(error => {
          console.log("Currency code fetch error:", error);
        });
    },

    closeSuccessModal() {
      clearTimeout(this.successRedirectTimeout);
      this.$refs.successMsgModal.hide();
      this.disableConfirmed = false;
      const cinemaName = window.localStorage.getItem("isCinemaName") || "";
      if (cinemaName.includes("QFX")) {
        this.$router.push({ name: "manual" });
      } else {
        this.$router.push({ name: "qrCode" });
      }
    },

    showPrintOption(privateShow = false) {
      if (!this.showStatusInfo.isValid) {
        this.openValidationPopup();
        return;
      }
      if (
        this.bookingDetails.hasOwnProperty("print_taken_type") &&
        this.bookingDetails.print_taken_type
      ) {
        this.$refs.ticketPrintConfirmation.show();
        this.privateShowprops = privateShow;
      } else {
        this.checkSubmitAvailable(privateShow, false);
      }
    },

    checkSubmitAvailable(privateShow = false, printTicket = false) {
      if (!this.showStatusInfo.isValid) {
        this.openValidationPopup();
        return;
      }
      if (this.ErrorSeats.length > 0 && this.selectedValue === "All") {
        this.$refs.EntryMsg.show();
        this.privateShowprops = privateShow;
      } else {
        this.onSubmitSeats(privateShow, printTicket);
      }
    },

    onSubmitSeats(privateShow = false, printTicket = false) {
      if (!this.showStatusInfo.isValid) {
        this.openValidationPopup();
        return;
      }

      this.disableConfirmed = true;
      const clientSetupName = window.localStorage.getItem("CLIENT_NAME") || "";

      let seatsSelected = this.seats.filter(x => x.selected === true);
      let payload = {
        seats: !this.bookingDetails.isPrivateShow
          ? this.selectedValue === "All"
            ? this.bookingDetails.seatArrayForQrCode.filter(x => !x.disabled)
            : seatsSelected
          : [],
        booking_id: this.qrcode,
        isPrivateShow: this.bookingDetails.isPrivateShow,
        current_admits: parseInt(this.currentAdmits)
      };

      BookingService.updateSeatOccupancy(payload)
        .then(response => {
          const { data } = response;
          if (data.status) {
            if (this.$refs.modal1) this.$refs.modal1.hide();
            this.successModalMessage = data.message || "Seats status updated";
            this.isCheckedIn = true;
            this.buttonchange();
            this.$refs.successMsgModal.show();

            if (printTicket && clientSetupName.includes("mif")) {
              let checkAllSeatEntered =
                this.bookingDetails["seatArrayForQrCode"].length ===
                payload["seats"].length;
              this.getSingleBoFnbApi(payload["seats"], checkAllSeatEntered);
            }

            this.successRedirectTimeout = setTimeout(() => {
              this.closeSuccessModal();
            }, 8000);

            if (privateShow) {
              this.buttonchange();
            }
          } else {
            this.disableConfirmed = false;
            this.$toasted.show(data.message);
          }
        })
        .catch(err => {
          this.disableConfirmed = false;
          if (err && err.response && err.response.data) {
            this.$toasted.show(err.response.data.message);
          } else {
            this.$toasted.show("Something went wrong with seat check-in");
          }
        });
    },

    async getSingleBoFnbApi(seats, allSeatEntered = false) {
      var userAgent = navigator.userAgent.toLowerCase();
      let sb_id = this.bookingDetails.sb_id || null;
      let ft_id = this.bookingDetails.ft_id || null;

      const enteredType = allSeatEntered ? 1 : 2;
      const ssbIdsArray = seats.map(se => se.ssb_id).join(",");
      if (userAgent.indexOf(" electron/") > -1) {
        try {
          this.$socket.emit("on_print_ticket", {
            ttype: "dynamicURL",
            dataURL: `${BASEURL}/api/print-bo-fnb-ticket/${sb_id}/${ft_id}?ticket_print_type=${null}&print_taken_type=${"SCAN"}&ssbIdsArray=${ssbIdsArray}&enteredtype=${enteredType}`
          });
        } catch (error) {
          console.log(error, "from common api ticket electron");
        }
      } else {
        try {
          const response = await BookingService.getBoFnbSingleTicketApi({
            sb_id: sb_id,
            ft_id: ft_id,
            ticket_print_type: null,
            ssbIdsArray: ssbIdsArray,
            enteredType: enteredType
          });
          const { data } = response;
          if (data) {
            this.EjsData = data;
          }
          this.$nextTick(() => {
            const p = new Printd();
            p.print(this.$refs.print_boxoffice_fnb_ticket, []);
          });
        } catch (error) {
          console.log(error, "from common api ticket web");
        }
      }
    },

    buttonchange() {
      this.isCheckedIn = true;
      const btn = document.getElementById("btn-chg");
      if (btn) {
        btn.innerText = "CHECKED-IN";
        btn.style.background = "#059669";
      }
    },

    sortSeatsString(seatA, seatB) {
      let a = seatA.sl_seat_name || "";
      let b = seatB.sl_seat_name || "";
      let reA = /[^a-zA-Z]/g;
      let reN = /[^0-9]/g;
      let aA = a.replace(reA, "");
      let bA = b.replace(reA, "");
      if (aA === bA) {
        let aN = parseInt(a.replace(reN, ""), 10);
        let bN = parseInt(b.replace(reN, ""), 10);
        return aN === bN ? 0 : aN > bN ? 1 : -1;
      } else {
        return aA > bA ? 1 : -1;
      }
    },

    getBookingDetails(booking_id) {
      let loader = this.$loading.show();
      this.isLoading = true;
      this.noEntryAvailable = false;
      this.someEntrynotAvailable = false;
      this.isCheckedIn = false;
      this.isLateEntryAllowedByOperator = false;
      this.isEarlyEntryAllowedByOperator = false;

      const userCineId = window.localStorage.getItem("USERCINEINFO");
      let payload = {
        booking_code: booking_id,
        cinema_id: userCineId ? Number(userCineId) : undefined
      };
      BookingService.GetBookingDetails(payload)
        .then(response => {
          const { data } = response;
          const hasRecords =
            data &&
            data.Records &&
            data.Records.result &&
            data.Records.result.length > 0;

          if (hasRecords) {
            this.bookingDetails = data.Records.result[0];
            this.remainingAdmits = this.bookingDetails.remainingAdmits || 0;
            if (this.bookingDetails.isPrivateShow) {
              this.currentAdmits = this.remainingAdmits || 1;
            }
            this.MovieContentArtworks = data.MovieContentArtworks || [];

            // Format showdate:
            if (this.bookingDetails.ss_start_date) {
              this.bookingDetails.showdate = moment(
                this.bookingDetails.ss_start_date
              ).format("ddd DD MMM YYYY");
            }

            // Map and sort seats - AUTO SELECT ALL VALID SEATS FOR OPERATOR SPEED!
            let count = 0;
            this.ErrorSeats = [];
            const seatList = data.Records.result[0].seatArrayForQrCode || [];
            this.selectedValue = "All";
            this.seats = seatList.map(seat => {
              if (
                window.localStorage.getItem("SEID") &&
                window.localStorage.getItem("SEID") !== null &&
                seat.entry_id
              ) {
                let SEID = window.localStorage.getItem("SEID");
                if (SEID.toLowerCase() !== seat.entry_id.toLowerCase()) {
                  count++;
                  this.ErrorSeats.push(seat);
                  seat.disabled = true;
                } else {
                  seat.disabled = false;
                }
              } else {
                seat.disabled = false;
              }

              // Also disable already occupied seats:
              if (seat.ssb_is_occupied === "Y" || seat.is_occupied === true) {
                seat.disabled = true;
              }

              // Pre-select non-disabled seats so operator can 1-tap check-in!
              seat.selected = !seat.disabled;
              return seat;
            });

            this.seats = this.seats.sort(this.sortSeatsString);

            if (count && count === this.seats.length) {
              this.noEntryAvailable = true;
              this.$refs.noEntryAvailable.show();
            } else if (count > 0) {
              this.someEntrynotAvailable = true;
            }

            // =======================================================
            // EXECUTE VALIDATION CHECKS (BACKEND FLAGS + CLIENT CHECKS)
            // =======================================================
            if (
              data.errorCode === "WRONG_CINEMA" ||
              this.bookingDetails.cinemaMismatch
            ) {
              const ticketCinemaName =
                this.bookingDetails.cine_name ||
                this.bookingDetails.cinema_name ||
                "";
              this.showStatusInfo = {
                isValid: false,
                code: "CINEMA_MISMATCH",
                title: "Cinema Mismatch",
                message:
                  data.message ||
                  "This QR/Booking Code does not belong to this cinema",
                subMessage: ticketCinemaName
                  ? 'Ticket booked at "' +
                    ticketCinemaName +
                    '". Please proceed to the correct cinema location.'
                  : "This QR/Booking Code belongs to another cinema branch."
              };
              this.validationError = this.showStatusInfo;
              this.openValidationPopup();
            } else if (
              data.errorCode === "SHOW_ENDED" ||
              this.bookingDetails.showEnded
            ) {
              this.showStatusInfo = {
                isValid: false,
                code: "SHOW_PASSED",
                title: "Show Ended",
                message: "Show Has Ended",
                subMessage:
                  data.message ||
                  "The scheduled showtime has ended. Entry is closed."
              };
              this.validationError = this.showStatusInfo;
              this.openValidationPopup();
            } else if (
              data.errorCode === "ALREADY_SCANNED" ||
              this.bookingDetails.alreadyScanned
            ) {
              this.showStatusInfo = {
                isValid: false,
                code: "ALREADY_SCANNED",
                title: "Already Scanned",
                message: "This ticket has already been scanned",
                subMessage:
                  data.message ||
                  "All seats on this booking have already been checked in."
              };
              this.validationError = this.showStatusInfo;
              this.openValidationPopup();
            } else {
              const valResult = this.validateBooking(this.bookingDetails);
              this.showStatusInfo = valResult;

              if (!valResult.isValid) {
                this.validationError = valResult;
                this.openValidationPopup();
              }
            }

            this.isLoading = false;
          } else {
            this.isLoading = false;
            const errMsg = data ? data.message : "No booking records found";
            this.showStatusInfo = {
              isValid: false,
              code: "API_ERROR",
              title: "Scan Error",
              message: errMsg || "Invalid or Expired Booking Code",
              subMessage: "Please verify the booking code and try again."
            };
            this.validationError = this.showStatusInfo;
            this.openValidationPopup();
          }
        })
        .catch(error => {
          this.isLoading = false;
          const serverMsg =
            error &&
            error.response &&
            error.response.data &&
            error.response.data.message;
          const fallbackMsg =
            serverMsg || "Booking details could not be loaded";

          this.showStatusInfo = {
            isValid: false,
            code: "API_ERROR",
            title: "Network Error",
            message: fallbackMsg,
            subMessage: "Please check connectivity or try scanning again."
          };
          this.validationError = this.showStatusInfo;
          this.openValidationPopup();
        })
        .finally(() => {
          loader.hide();
        });
    },

    getSeatLabel(seat) {
      if (!this.isNovo01Mall) return seat.sl_seat_name || "";

      const row =
        seat.sl_row_name !== undefined &&
        seat.sl_row_name !== null &&
        String(seat.sl_row_name).trim() !== ""
          ? seat.sl_row_name
          : seat.sl_row_num;

      const hasRow =
        row !== undefined && row !== null && String(row).trim() !== "";
      return hasRow ? `${row}-${seat.sl_seat_name}` : seat.sl_seat_name || "";
    }
  }
};
</script>

<style scoped>
/* ==========================================================================
   OPERATOR-OPTIMIZED, FAST-SCAN DETAILS PAGE STYLES
   Designed for speed: Glanceable info, preselected seats,
   and a STICKY FIXED DOCKED CHECK-IN BUTTON that never requires scrolling!
   ========================================================================== */

.details-page-wrapper {
  min-height: 100vh;
  background-color: #f1f4f9;
  color: #0f172a;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  /* Extra bottom padding to ensure content is never covered by the fixed check-in dock + bottom nav */
  padding-bottom: 140px;
  position: relative;
  box-sizing: border-box;
  overflow-x: hidden;
  width: 100%;
}

/* COMPACT TOP APP BAR */
.details-topbar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  position: sticky;
  top: 0;
  z-index: 50;
  height: 48px;
  box-sizing: border-box;
  width: 100%;
}

.topbar-inner {
  width: 100%;
  max-width: 540px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  box-sizing: border-box;
  height: 100%;
}

.topbar-back-btn,
.topbar-action-btn {
  background: #f1f5f9;
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  flex-shrink: 0;
}

.topbar-back-btn:hover,
.topbar-action-btn:hover {
  background-color: #e2e8f0;
}

.topbar-title-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  padding: 0 4px;
}

.topbar-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.topbar-code-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 5px;
  letter-spacing: 0.2px;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.topbar-code-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* MAIN CONTAINER (COMPACT, GLANCEABLE) */
.details-container {
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  padding: 8px 12px;
  box-sizing: border-box;
}

/* COMMON CARD STYLES */
.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  margin-bottom: 8px;
  padding: 10px 12px;
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;
  transition: all 0.2s ease;
}

/* RESPONSIVE DESKTOP LAYOUT SCALING */
@media (min-width: 768px) {
  .details-topbar {
    height: 52px;
  }

  .topbar-inner {
    max-width: 880px;
    padding: 0 20px;
  }

  .topbar-title {
    font-size: 16px;
  }

  .topbar-code-chip {
    max-width: 240px;
    font-size: 12px;
    padding: 3px 9px;
  }

  .details-container {
    max-width: 880px;
    padding: 14px 20px;
  }

  .card {
    border-radius: 16px;
    padding: 16px 20px;
    margin-bottom: 12px;
  }

  .status-hero-card {
    padding: 13px 18px;
    border-radius: 14px;
    gap: 14px;
  }

  .status-hero-icon-box {
    width: 38px;
    height: 38px;
  }

  .status-hero-headline {
    font-size: 15.5px;
  }

  .status-hero-sub {
    font-size: 12px;
  }

  .status-hero-review-btn {
    font-size: 12px;
    padding: 6px 14px;
    border-radius: 7px;
  }

  .status-hero-allow-btn {
    font-size: 12px;
    padding: 6px 14px;
    border-radius: 7px;
  }

  .movie-title {
    font-size: 20px;
    margin-bottom: 8px;
  }

  .movie-tags-row {
    gap: 7px;
  }

  .badge-tag {
    font-size: 11px;
    padding: 3px 9px;
    border-radius: 6px;
  }

  .rating-circle {
    width: 48px;
    height: 48px;
  }

  .rating-val {
    font-size: 16px;
  }

  .dob-note {
    font-size: 10px;
  }

  .quick-glance-pills {
    gap: 12px;
    margin-top: 12px;
  }

  .glance-pill {
    padding: 11px 14px;
    border-radius: 10px;
    gap: 10px;
  }

  .glance-pill svg {
    width: 18px;
    height: 18px;
  }

  .glance-main-val {
    font-size: 14px;
  }

  .glance-sub-val {
    font-size: 11px;
  }

  .section-title {
    font-size: 15px;
  }

  .seat-count-pill {
    font-size: 11.5px;
    padding: 3px 9px;
  }

  .tt-pill {
    font-size: 11.5px;
    padding: 3px 9px;
  }

  .seat-chips-grid {
    gap: 8px;
    margin-top: 8px;
  }

  .seat-chip {
    min-width: 54px;
    height: 42px;
    font-size: 14px;
    border-radius: 9px;
  }

  .chip-all {
    min-width: 62px;
    font-size: 14px;
  }
}

@media (min-width: 1024px) {
  .details-topbar {
    height: 58px;
  }

  .topbar-inner {
    max-width: 1120px;
    padding: 0 24px;
  }

  .topbar-title {
    font-size: 18px;
  }

  .topbar-code-chip {
    max-width: 340px;
    font-size: 13px;
    padding: 4px 12px;
    border-radius: 6px;
  }

  .topbar-back-btn,
  .topbar-action-btn {
    width: 38px;
    height: 38px;
    border-radius: 10px;
  }

  .details-container {
    max-width: 1120px;
    padding: 18px 24px;
  }

  .card {
    border-radius: 18px;
    padding: 20px 26px;
    margin-bottom: 16px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
  }

  .status-hero-card {
    padding: 16px 24px;
    border-radius: 16px;
    gap: 16px;
    margin-bottom: 14px;
  }

  .status-hero-icon-box {
    width: 44px;
    height: 44px;
    border-radius: 10px;
  }

  .status-hero-icon-box svg {
    width: 24px;
    height: 24px;
  }

  .status-hero-headline {
    font-size: 17.5px;
  }

  .status-hero-sub {
    font-size: 13.5px;
    margin-top: 4px;
  }

  .status-hero-review-btn {
    font-size: 13px;
    padding: 7px 16px;
    border-radius: 8px;
  }

  .status-hero-allow-btn {
    font-size: 13px;
    padding: 7px 16px;
    border-radius: 8px;
  }

  .movie-title {
    font-size: 24px;
    margin-bottom: 10px;
    letter-spacing: -0.3px;
  }

  .movie-tags-row {
    gap: 8px;
  }

  .badge-tag {
    font-size: 12.5px;
    padding: 4px 11px;
    border-radius: 6px;
  }

  .rating-circle {
    width: 56px;
    height: 56px;
    border-width: 2.5px;
  }

  .rating-val {
    font-size: 18px;
  }

  .rating-sub {
    font-size: 9px;
  }

  .dob-note {
    font-size: 11px;
    margin-top: 5px;
  }

  .quick-glance-pills {
    gap: 16px;
    margin-top: 16px;
  }

  .glance-pill {
    padding: 14px 18px;
    border-radius: 12px;
    gap: 12px;
  }

  .glance-pill svg {
    width: 22px;
    height: 22px;
  }

  .glance-main-val {
    font-size: 16px;
  }

  .glance-sub-val {
    font-size: 12.5px;
    margin-top: 2px;
  }

  .section-title {
    font-size: 16.5px;
  }

  .seat-count-pill {
    font-size: 13px;
    padding: 4px 11px;
  }

  .tt-pill {
    font-size: 12.5px;
    padding: 4px 11px;
  }

  .seat-chips-grid {
    gap: 10px;
    margin-top: 12px;
  }

  .seat-chip {
    min-width: 66px;
    height: 48px;
    font-size: 15.5px;
    font-weight: 800;
    border-radius: 11px;
    border-width: 2px;
  }

  .chip-all {
    min-width: 74px;
    font-size: 15.5px;
  }
}

@media (min-width: 1400px) {
  .topbar-inner {
    max-width: 1240px;
  }

  .details-container {
    max-width: 1240px;
    padding: 22px 30px;
  }

  .card {
    border-radius: 20px;
    padding: 22px 30px;
  }

  .movie-title {
    font-size: 26px;
  }

  .glance-main-val {
    font-size: 16.5px;
  }

  .glance-pill {
    padding: 16px 20px;
  }
}

/* STATUS HERO BANNER */
.status-hero-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  margin-bottom: 10px;
  transition: all 0.2s ease;
}

.status-hero-card.status-valid {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: #ffffff;
  box-shadow: 0 3px 10px rgba(16, 185, 129, 0.2);
}

.status-hero-card.status-warning {
  background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
  color: #ffffff;
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.2);
}

.status-hero-card.status-error {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: #ffffff;
  box-shadow: 0 3px 10px rgba(239, 68, 68, 0.2);
}

.status-hero-icon-box {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-hero-text {
  flex: 1;
}

.status-hero-headline {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.status-hero-sub {
  font-size: 11px;
  opacity: 0.94;
  margin-top: 2px;
  line-height: 1.3;
}

.status-hero-review-btn {
  background: #ffffff;
  color: #0f172a;
  border: none;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.status-hero-allow-btn {
  background: #ffffff;
  color: #059669;
  border: 1.5px solid #ffffff;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.status-hero-allow-btn:hover {
  background: #ecfdf5;
  color: #047857;
  transform: translateY(-1px);
}

/* RESCHEDULED SHOW ALERT */
.swap-alert-card {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.swap-alert-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 3px;
}

.swap-text {
  font-size: 12px;
  color: #78350f;
  margin: 0;
  line-height: 1.35;
}

/* MOVIE HERO CARD */
.movie-hero-card {
  padding: 12px 14px;
}

.movie-hero-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e2e8f0;
}

.movie-title-group {
  flex: 1;
}

.movie-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px 0;
  text-transform: uppercase;
  line-height: 1.25;
  letter-spacing: -0.2px;
}

.movie-tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.badge-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tag-lang {
  background-color: #e0f2fe;
  color: #0369a1;
}

.tag-format {
  background-color: #0f172a;
  color: #ffffff;
}

.tag-cinema-sub {
  background-color: #f1f5f9;
  color: #334155;
  font-weight: 600;
}

/* AGE RATING BADGE */
.age-badge-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.rating-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid #0f172a;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.rating-val {
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
}

.rating-sub {
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1;
  margin-top: 1px;
}

.dob-note {
  font-size: 9px;
  font-weight: 600;
  color: #64748b;
  margin-top: 3px;
  text-align: center;
}

/* QUICK GLANCE RESPONSIVE PILLS */
.quick-glance-pills {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
}

.glance-pill {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 9px;
  display: flex;
  align-items: center;
  gap: 7px;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
}

.glance-pill svg {
  flex-shrink: 0;
}

.glance-pill-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.glance-main-val {
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: 1.2;
}

.pill-time .glance-main-val {
  color: #032d6e;
}

.glance-sub-val {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: 1.2;
  margin-top: 1px;
}

.pill-seats {
  grid-column: 1 / -1;
  background-color: #eff6ff;
  border-color: #bfdbfe;
}

.pill-seats-headline {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  flex-wrap: wrap;
}

.pill-seats-dot {
  color: #3b82f6;
  font-weight: 700;
  font-size: 12px;
}

.pill-seats-highlight {
  color: #1d4ed8;
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

@media (min-width: 480px) {
  .quick-glance-pills {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .glance-pill {
    padding: 8px 10px;
  }

  .pill-seats {
    grid-column: auto;
    background-color: #f8fafc;
    border-color: #e2e8f0;
  }

  .pill-seats-headline {
    flex-direction: column;
    gap: 0;
  }

  .pill-seats-dot {
    display: none;
  }

  .pill-seats-highlight {
    color: #64748b;
    font-weight: 500;
    font-size: 10px;
  }
}

/* TICKETS & SEATS COMPACT CARD */
.tickets-selection-card {
  padding: 10px 12px;
}

.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.seat-count-pill {
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 12px;
}

.seat-count-pill.pill-locked {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.pill-private {
  background-color: #fef3c7;
  color: #92400e;
}

.ticket-types-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tt-pill {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

/* SEAT BUTTONS GRID */
.seat-chips-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.seat-chip {
  background-color: #ffffff;
  color: #0f172a;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  min-width: 44px;
  height: 36px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  box-sizing: border-box;
}

.chip-all {
  min-width: 48px;
  background-color: #f8fafc;
  border-color: #94a3b8;
}

.seat-chip:hover:not(:disabled) {
  border-color: #0284c7;
  background-color: #f0f9ff;
}

.seat-chip.chip-selected {
  background: linear-gradient(135deg, #032d6e 0%, #1d4ed8 100%);
  color: #ffffff;
  border-color: #032d6e;
  box-shadow: 0 2px 6px rgba(3, 45, 110, 0.25);
}

.seat-chip.chip-disabled,
.seat-chip:disabled {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.7;
  box-shadow: none;
}

/* PRIVATE SHOW STEPPER */
.private-show-counter-wrap {
  margin-top: 6px;
}

.private-info-note {
  font-size: 11px;
  color: #475569;
  text-align: center;
  margin-bottom: 8px;
}

.stepper-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.stepper-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #032d6e;
  background-color: #ffffff;
  color: #032d6e;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
}

.stepper-input-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stepper-input {
  width: 80px;
  height: 40px;
  text-align: center;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
}

.stepper-caption {
  font-size: 10px;
  color: #64748b;
  margin-top: 3px;
}

/* 4DX AUDITORIUM POLICIES */
.policies-card {
  border-left: 3px solid #ef4444;
  padding: 10px 12px;
}

.policy-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.policy-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.policy-item p {
  font-size: 11px;
  color: #334155;
  margin: 0;
  line-height: 1.3;
}

/* ==========================================================================
   FIXED DOCKED CHECK-IN BAR (NO SCROLLING NEEDED FOR OPERATOR!)
   ========================================================================== */
.fixed-checkin-dock {
  position: fixed;
  bottom: 50px; /* Sits cleanly above the 50px bottom nav */
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid #e2e8f0;
  padding: 6px 12px;
  box-shadow: 0 -3px 12px rgba(15, 23, 42, 0.06);
  z-index: 95;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
}

.dock-inner {
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dock-warning-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
  text-align: center;
  line-height: 1.2;
}

.dock-warning-note svg {
  flex-shrink: 0;
}

.dock-view-err-link {
  background: none;
  border: none;
  color: #1d4ed8;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  margin-left: 2px;
}

.dock-checkin-btn {
  width: 100%;
  height: 46px;
  border-radius: 11px;
  border: none;
  background: linear-gradient(135deg, #032d6e 0%, #1e40af 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(3, 45, 110, 0.3);
  transition: all 0.2s ease;
  outline: none;
  box-sizing: border-box;
}

.dock-checkin-btn:hover:not(:disabled) {
  box-shadow: 0 5px 16px rgba(3, 45, 110, 0.4);
  transform: translateY(-1px);
}

.dock-checkin-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.dock-btn-title {
  font-size: 14.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.dock-btn-counter {
  font-size: 13.5px;
  font-weight: 700;
  opacity: 0.95;
}

.dock-checkin-btn.btn-checked {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
}

.dock-checkin-btn.btn-disabled,
.dock-checkin-btn:disabled {
  background: #cbd5e1;
  color: #64748b;
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}

.dock-checkin-btn.btn-locked {
  background: #e2e8f0;
  color: #64748b;
  border: 1px solid #cbd5e1;
  box-shadow: none;
}

.dock-warning-late {
  color: #b45309 !important;
}

.dock-checkin-btn.btn-allow-late {
  background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.35);
  cursor: pointer;
}

.dock-checkin-btn.btn-allow-late:hover {
  background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
  transform: translateY(-1px);
}

/* FIXED BOTTOM APP BAR */
.details-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background-color: #ffffff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 100;
  box-sizing: border-box;
}

.bottom-bar-inner {
  width: 100%;
  max-width: 540px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 16px;
  box-sizing: border-box;
  height: 100%;
}

.nav-tab-btn {
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  padding: 2px 14px;
  outline: none;
}

.nav-icon-wrap {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-tab-label {
  font-size: 9.5px;
  font-weight: 600;
  margin-top: 1px;
}

.nav-scanner-center-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.scanner-btn-inner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #032d6e 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(3, 45, 110, 0.25);
  transition: transform 0.2s;
}

.scanner-btn-inner:active {
  transform: scale(0.95);
}

/* DESKTOP MEDIA QUERIES FOR DOCK AND BOTTOM BAR */
@media (min-width: 768px) {
  .fixed-checkin-dock {
    bottom: 54px;
    padding: 10px 18px;
  }

  .dock-inner {
    max-width: 880px;
    gap: 6px;
  }

  .dock-checkin-btn {
    height: 52px;
    border-radius: 12px;
  }

  .dock-btn-title {
    font-size: 16px;
  }

  .dock-btn-counter {
    font-size: 14.5px;
  }

  .dock-warning-note {
    font-size: 12.5px;
    gap: 6px;
  }

  .details-bottom-bar {
    height: 54px;
  }

  .bottom-bar-inner {
    max-width: 720px;
  }

  .nav-tab-label {
    font-size: 10.5px;
  }

  .scanner-btn-inner {
    width: 38px;
    height: 38px;
  }
}

@media (min-width: 1024px) {
  .fixed-checkin-dock {
    bottom: 58px;
    padding: 12px 24px;
  }

  .dock-inner {
    max-width: 1120px;
    gap: 8px;
  }

  .dock-checkin-btn {
    height: 58px;
    border-radius: 14px;
  }

  .dock-checkin-btn svg {
    width: 22px;
    height: 22px;
  }

  .dock-btn-title {
    font-size: 17.5px;
  }

  .dock-btn-counter {
    font-size: 15px;
  }

  .dock-warning-note {
    font-size: 13.5px;
    gap: 7px;
  }

  .dock-view-err-link {
    font-size: 13.5px;
  }

  .details-bottom-bar {
    height: 58px;
  }

  .bottom-bar-inner {
    max-width: 840px;
  }

  .nav-tab-label {
    font-size: 12px;
  }

  .nav-icon-wrap {
    width: 22px;
    height: 22px;
  }

  .scanner-btn-inner {
    width: 42px;
    height: 42px;
  }
}

@media (min-width: 1400px) {
  .dock-inner {
    max-width: 1240px;
  }
}

/* ==========================================================================
   POPUP MODAL STYLES (b-modal custom container)
   ========================================================================== */

.val-modal-box {
  padding: 6px 2px;
  text-align: center;
}

.val-modal-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.val-modal-icon-wrap.val-icon-entry_not_started {
  background-color: #fef3c7;
  color: #d97706;
}

.val-modal-icon-wrap.val-icon-show_passed {
  background-color: #fee2e2;
  color: #dc2626;
}

.val-modal-icon-wrap.val-icon-cinema_mismatch {
  background-color: #fee2e2;
  color: #b91c1c;
}

.val-modal-icon-wrap.val-icon-already_scanned {
  background-color: #fef3c7;
  color: #d97706;
}

.val-modal-icon-wrap.val-icon-api_error {
  background-color: #f1f5f9;
  color: #475569;
}

.val-modal-icon-wrap.val-icon-late_entry {
  background-color: #fef3c7;
  color: #d97706;
}

.val-modal-icon-wrap.val-icon-late_entry_allowed,
.val-modal-icon-wrap.val-icon-early_entry_allowed {
  background-color: #d1fae5;
  color: #059669;
}

.val-modal-head {
  margin-bottom: 14px;
}

.val-type-pill {
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 9px;
  border-radius: 12px;
  margin-bottom: 5px;
}

.pill-entry_not_started {
  background-color: #fef3c7;
  color: #b45309;
}

.pill-late_entry {
  background-color: #fef3c7;
  color: #b45309;
}

.pill-late_entry_allowed,
.pill-early_entry_allowed {
  background-color: #d1fae5;
  color: #065f46;
}

.pill-already_scanned {
  background-color: #fef3c7;
  color: #b45309;
}

.pill-show_passed {
  background-color: #fee2e2;
  color: #b91c1c;
}

.pill-cinema_mismatch {
  background-color: #fce7f3;
  color: #9d174d;
}

.pill-api_error {
  background-color: #f1f5f9;
  color: #475569;
}

.val-primary-title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 5px 0;
  line-height: 1.25;
}

.val-submessage {
  font-size: 12px;
  color: #b91c1c;
  font-weight: 600;
  /* color: #64748b; */
  margin: 0;
  line-height: 1.4;
  padding: 0 8px;
}

/* EMBEDDED MOVIE DETAILS CARD IN POPUP */
.val-movie-card {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  text-align: left;
  margin-bottom: 16px;
}

.val-movie-top-bar {
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
  margin-bottom: 8px;
}

.val-movie-name {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 5px 0;
  text-transform: uppercase;
  line-height: 1.25;
}

.val-chips {
  display: flex;
  gap: 5px;
}

.val-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
}

.val-chip-lang {
  background-color: #e0f2fe;
  color: #0369a1;
}

.val-chip-format {
  background-color: #0f172a;
  color: #ffffff;
}

.val-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.val-grid-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 12px;
}

.val-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #64748b;
  font-weight: 600;
  flex-shrink: 0;
}

.val-data {
  color: #0f172a;
  font-weight: 700;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

.val-cinema-alert {
  color: #dc2626 !important;
  background-color: #fef2f2;
  padding: 1px 5px;
  border-radius: 4px;
}

.val-time-data {
  color: #032d6e;
}

.val-date-sub {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  display: block;
}

.val-seats-badge {
  background-color: #eff6ff;
  color: #1e40af;
  padding: 2px 7px;
  border-radius: 5px;
  font-size: 11px;
}

/* MODAL ACTION BUTTONS */
.val-actions-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.val-action-btn-allow {
  width: 100%;
  height: 48px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: #ffffff;
  font-size: 14.5px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  outline: none;
  box-shadow: 0 3px 12px rgba(16, 185, 129, 0.35);
  transition: all 0.2s ease;
  margin-bottom: 4px;
}

.val-action-btn-allow:hover {
  background: linear-gradient(135deg, #047857 0%, #059669 100%);
  transform: translateY(-1px);
}

.val-action-btn-primary {
  width: 100%;
  height: 46px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #032d6e 0%, #1e40af 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
}

.val-action-btn-primary:hover {
  background: linear-gradient(135deg, #02245b 0%, #173794 100%);
}

.val-action-btn-secondary {
  width: 100%;
  height: 36px;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
}

.val-action-btn-secondary:hover {
  color: #0f172a;
}

.modern-modal-padding {
  padding: 16px 10px;
}

.modern-table {
  font-size: 12px;
  border-color: #e2e8f0;
}

.modern-table thead th {
  background-color: #f8fafc;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
}
</style>



// WEBPACK FOOTER //
// src/components/details.vue